import { useEffect, useState } from 'react'
import { algoliaBrowseApi } from 'apis/algolia'
import { getSortIndice } from 'apps/public/browse/helpers/constants'
import { useStore, useSelector } from 'react-redux'
import { Actions as AlgoliaActions } from 'actions/algolia2/data'
import { getGeoZoneDestinationCode } from 'lib/storeLocale/client'
import { getGeoLocation } from 'selectors/user/cookie'
import { getPageParams, getPathParams } from 'apps/public/browse/selectors/app'
import { getAlgoliaSetup } from 'selectors/algolia'
import { parseUrlFacets, selectedFacetsToAlgoliaQueryString } from 'lib/algolia/v2'
import { browseBooleanFacets } from 'lib/algolia/v2/constants'

type useGetAlgoliaDataResponse = {
  isAlgoliaLoading: boolean
}

const HITS_PER_PAGE = 50
const setupBrowseData = async ({
  algoliaSetup,
  pathParams,
  query,
  destinationZone,
}: Record<string, any>) => {
  try {
    // Configuration Data
    const { facets, sharedFacetNames, slugifyDictionary } = algoliaSetup
    // Create Facets List for Redux Store, we will merge in URL Params & Queries ( slugify )
    const selectedFacets = parseUrlFacets({
      dictionary: slugifyDictionary,
      href: window.location.href,
      routeParams: pathParams,
      booleanFacets: browseBooleanFacets,
    })
    const { hitsPerPage, page, query: search, sort } = query
    // We only want AVAIL => FOR SALE artworks - Manually add query, Ignore Prints
    const hasPrints = selectedFacets?.has_prints?.length >= 1
    const overrideFacets = {
      ...(!hasPrints
        ? {
            original_status: 'avail',
          }
        : {}),
      ...selectedFacets,
    }
    // TODO - Geo Based
    const filters = selectedFacetsToAlgoliaQueryString(overrideFacets, {
      destinationZone,
    })
    // We have the default Indice but we need to apply SORT logic, if it exist
    const searchIndice = getSortIndice(sort, destinationZone, hasPrints)
    // Fetch Algolia
    const algoliaResults = await algoliaBrowseApi({
      filters,
      hitsPerPage: hitsPerPage || HITS_PER_PAGE,
      page,
      query: search,
      searchIndice,
      destinationZone,
    })
    // Expect to Match ALGOLIA Reducer - Initial Setup
    return {
      data: algoliaResults,
      facets: facets || {},
      hitsPerPage: hitsPerPage || HITS_PER_PAGE,
      searchIndice,
      selectedFacets: selectedFacets || {},
      sharedFacetNames,
      slugifyDictionary,
    }
  } catch (error) {
    console.error('setupBrowseData', {
      error,
    })
    return {}
  }
}

export const useGetAlgoliaData = (): useGetAlgoliaDataResponse => {
  const [isAlgoliaLoading, setIsAlgoliaLoading] = useState(true)
  const store = useStore()

  const state = { ...store.getState() }
  const pathParams = getPathParams(state)
  const queryParams = getPageParams(state)
  const algoliaSetup = getAlgoliaSetup(state)
  const geoLocation = useSelector(getGeoLocation)
  const destinationZone = getGeoZoneDestinationCode(geoLocation?.countryCode)

  const loadAlgoliaToRedux = async () => {
    setIsAlgoliaLoading(true)
    const algoliaData = await setupBrowseData({
      algoliaSetup,
      pathParams,
      query: queryParams,
      destinationZone,
    })
    store.dispatch(AlgoliaActions.INITIAL_SETUP(algoliaData))
    setIsAlgoliaLoading(false)
  }

  useEffect(() => {
    if (window !== undefined) {
      loadAlgoliaToRedux()
    }
  }, [])

  return { isAlgoliaLoading }
}
